/**
 * THE CARD (P36) — reference §7: "hairline, radius 8, **no shadow**, and a
 * header row that renders a real `<h2>`".
 *
 * The last clause is the reason this component is built in P36 and not in P39,
 * where the ticket screen is restructured. The audit's finding there is "eight
 * cards, zero headings": `/tickets/:n` renders eight `rounded-lg border bg-white`
 * blocks, several of which carry a bold `<div>` where the heading belongs, so a
 * screen reader offers no way to jump between them and the page is one long
 * undifferentiated region. If the Card owns the `<h2>`, P39 fixes that by using
 * the Card — it never has to think about it, and it cannot get it wrong for one
 * of the eight.
 *
 * **No shadow** is a rule, not an omission. `--overlay-shadow` exists for the
 * three things that float above the page — dialog, menu, toast — and a card
 * that also casts one takes the distinction away.
 */
import { useId, type ReactNode } from 'react';

export interface CardProps {
    /** Rendered as a real `<h2>`. Omit it only for a card that has no subject. */
    title?: ReactNode;
    /** Sits at the right of the header row: a button, a count, a link. */
    action?: ReactNode;
    /** One line under the heading, in muted type. */
    subtitle?: ReactNode;
    children: ReactNode;
    className?: string;
}

export function Card({ title, action, subtitle, children, className = '' }: CardProps) {
    const headingId = useId();
    return (
        <section
            aria-labelledby={title ? headingId : undefined}
            className={`rounded-card border border-hair bg-surface ${className}`.trim()}
        >
            {(title || action) && (
                <div className="flex items-start justify-between gap-3 border-b border-hair-2 px-4 py-3">
                    <div className="min-w-0">
                        {title && (
                            <h2 id={headingId} className="text-sm font-semibold text-ink">{title}</h2>
                        )}
                        {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
                    </div>
                    {action && <div className="shrink-0">{action}</div>}
                </div>
            )}
            <div className="px-4 py-3">{children}</div>
        </section>
    );
}
